/**
 * DepartmentMap Component
 * Interactive department tabs showing organizational benefits
 */

import React, { useState } from 'react';

const styles = {
  container: {
    padding: '60px 20px',
    marginBottom: '40px',
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '16px',
    color: '#BBBEC7',
    textAlign: 'center',
    marginBottom: '40px',
  },
  tabsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '40px',
    justifyContent: 'center',
    maxWidth: '1000px',
    margin: '0 auto 40px',
  },
  tab: {
    padding: '12px 24px',
    borderRadius: '6px',
    border: '1px solid #2A3444',
    backgroundColor: 'transparent',
    color: '#BBBEC7',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  tabActive: {
    borderColor: '#0057FF',
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
    boxShadow: '0 8px 16px rgba(0, 87, 255, 0.2)',
  },
  tabHover: {
    borderColor: '#00D4AA',
  },
  contentContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    animation: 'fadeIn 0.4s ease-in-out',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  benefitCard: {
    padding: '24px',
    borderRadius: '8px',
    border: '1px solid #2A3444',
    backgroundColor: '#1A2332',
    transition: 'all 0.3s ease',
  },
  benefitCardHover: {
    borderColor: '#00D4AA',
    backgroundColor: 'rgba(0, 212, 170, 0.05)',
    transform: 'translateY(-4px)',
  },
  benefitIcon: {
    fontSize: '36px',
    marginBottom: '12px',
    display: 'block',
  },
  benefitTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: '8px',
  },
  benefitDescription: {
    fontSize: '13px',
    color: '#BBBEC7',
    lineHeight: '1.5',
  },
  descriptionText: {
    fontSize: '15px',
    color: '#BBBEC7',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
};

/**
 * DepartmentMap Component
 */
function DepartmentMap({ prospect }) {
  const [activeTab, setActiveTab] = useState(0);

  const departments = [
    {
      name: 'Sales',
      icon: '💼',
      description: 'Streamline your sales process and close deals faster with our integrated tools.',
      benefits: [
        { title: 'Pipeline Visibility', icon: '📊', description: 'Real-time view of all opportunities' },
        { title: 'Lead Management', icon: '👥', description: 'Automated lead capture and nurturing' },
        { title: 'Forecasting', icon: '📈', description: 'Accurate revenue predictions' },
      ],
    },
    {
      name: 'Marketing',
      icon: '📢',
      description: 'Create, execute, and measure campaigns that drive results.',
      benefits: [
        { title: 'Campaign Management', icon: '🎯', description: 'End-to-end campaign orchestration' },
        { title: 'Analytics', icon: '📊', description: 'Detailed performance insights' },
        { title: 'Automation', icon: '⚙️', description: 'Smart workflow automation' },
      ],
    },
    {
      name: 'Operations',
      icon: '⚙️',
      description: 'Optimize operations and improve efficiency across the organization.',
      benefits: [
        { title: 'Process Automation', icon: '🔄', description: 'Eliminate manual tasks' },
        { title: 'Integration', icon: '🔗', description: 'Connect all your business systems' },
        { title: 'Reporting', icon: '📋', description: 'Comprehensive operational reports' },
      ],
    },
    {
      name: 'Customer Success',
      icon: '😊',
      description: 'Delight customers and maximize lifetime value.',
      benefits: [
        { title: 'Support Tracking', icon: '🎫', description: 'Ticket management and tracking' },
        { title: 'Health Monitoring', icon: '❤️', description: 'Proactive customer health alerts' },
        { title: 'Retention', icon: '🎁', description: 'Reduce churn with smart interventions' },
      ],
    },
    {
      name: 'Finance',
      icon: '💰',
      description: 'Gain visibility into financial performance and optimize budgets.',
      benefits: [
        { title: 'Billing', icon: '📄', description: 'Automated invoice and billing management' },
        { title: 'Analytics', icon: '💹', description: 'Financial performance dashboards' },
        { title: 'Compliance', icon: '✅', description: 'Audit trails and compliance reporting' },
      ],
    },
  ];

  const activeDept = departments[activeTab];

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Benefits by Department</h2>
      <p style={styles.subtitle}>How Talk Australia helps every part of your organization</p>

      {/* Department tabs */}
      <div style={styles.tabsContainer}>
        {departments.map((dept, idx) => (
          <button
            key={idx}
            style={{
              ...styles.tab,
              ...(activeTab === idx && styles.tabActive),
            }}
            onMouseEnter={(e) => {
              if (activeTab !== idx) {
                e.target.style.borderColor = '#00D4AA';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== idx) {
                e.target.style.borderColor = '#2A3444';
              }
            }}
            onClick={() => setActiveTab(idx)}
          >
            <span style={{ marginRight: '8px' }}>{dept.icon}</span>
            {dept.name}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.contentContainer}>
        <p style={styles.descriptionText}>{activeDept.description}</p>

        <div style={styles.contentGrid}>
          {activeDept.benefits.map((benefit, idx) => (
            <BenefitCard key={idx} benefit={benefit} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * BenefitCard sub-component
 */
function BenefitCard({ benefit }) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      style={{
        ...styles.benefitCard,
        ...(isHovered && styles.benefitCardHover),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span style={styles.benefitIcon}>{benefit.icon}</span>
      <div style={styles.benefitTitle}>{benefit.title}</div>
      <div style={styles.benefitDescription}>{benefit.description}</div>
    </div>
  );
}

DepartmentMap.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
};

export default DepartmentMap;
